import React, { useEffect, useState, useRef } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { Plus, MapPin, Loader2, Check, Paperclip, X } from "lucide-react";
import { toast } from "sonner";
import { Reveal } from "./Reveal";
import { useContent } from "./content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const Careers = () => {
  const c = useContent();
  const [jobs, setJobs] = useState([]);
  const [openIdx, setOpenIdx] = useState(0);
  const [form, setForm] = useState({ name: "", email: "" });
  const [resumeFile, setResumeFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [doneRole, setDoneRole] = useState(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    axios.get(`${API}/jobs`).then((r) => setJobs(r.data)).catch(() => {});
  }, []);

  const pickResume = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const okExt = /\.(pdf|doc|docx)$/i.test(f.name);
    if (!okExt) {
      toast.error("Please attach a PDF, DOC or DOCX file.");
      return;
    }
    if (f.size > 8 * 1024 * 1024) {
      toast.error("Résumé is too large (max 8 MB).");
      return;
    }
    setResumeFile(f);
  };

  const apply = async (role) => {
    if (!form.name || !form.email) {
      toast.error("Please add your name and email.");
      return;
    }
    setSubmitting(true);
    try {
      let resume_url = "";
      let resume_name = "";
      if (resumeFile) {
        const fd = new FormData();
        fd.append("file", resumeFile);
        const up = await axios.post(`${API}/applications/upload-resume`, fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        resume_url = up.data.url;
        resume_name = up.data.name;
      }
      await axios.post(`${API}/applications`, { ...form, role, resume_url, resume_name });
      setDoneRole(role);
      setForm({ name: "", email: "" });
      setResumeFile(null);
      toast.success("Application received — we'll be in touch!");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Something went wrong. Try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="careers" data-testid="careers-section" className="py-24 md:py-32">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
          <div className="lg:col-span-4">
            <Reveal>
              <span className="uppercase tracking-[0.24em] text-xs font-bold text-accent">{c.careers_eyebrow}</span>
              <h2 className="mt-4 font-display font-medium tracking-tight text-4xl sm:text-5xl lg:text-6xl leading-[1.02]">
                {c.careers_title}
              </h2>
              <p className="mt-6 text-foreground/65 leading-relaxed max-w-sm">
                {c.careers_subtitle}
              </p>
            </Reveal>
          </div>

          <div className="lg:col-span-8">
            <div className="flex flex-col divide-y divide-border border-y border-border">
              {jobs.map((j, i) => {
                const open = openIdx === i;
                return (
                  <div key={j.role} data-testid={`job-${i}`}>
                    <button
                      data-testid={`job-toggle-${i}`}
                      onClick={() => setOpenIdx(open ? -1 : i)}
                      className="w-full py-7 flex items-center justify-between gap-4 text-left group"
                    >
                      <div>
                        <h3 className="font-heading text-xl md:text-2xl font-medium tracking-tight group-hover:text-primary transition-colors duration-300">
                          {j.role}
                        </h3>
                        <div className="mt-2 flex items-center gap-4 text-sm text-foreground/55">
                          <span className="flex items-center gap-1.5">
                            <MapPin size={14} /> {j.location}
                          </span>
                          <span className="px-2.5 py-0.5 rounded-full bg-secondary/15 text-primary text-xs font-medium">
                            {j.type}
                          </span>
                        </div>
                      </div>
                      <span
                        className={`grid place-items-center h-10 w-10 rounded-full border border-border text-primary transition-transform duration-300 ${
                          open ? "rotate-45 bg-primary text-white border-primary" : ""
                        }`}
                      >
                        <Plus size={18} />
                      </span>
                    </button>

                    <AnimatePresence initial={false}>
                      {open && (
                        <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: "auto", opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
                          className="overflow-hidden"
                        >
                          <div className="pb-8">
                            {j.image ? (
                              <div className="mb-6 rounded-2xl overflow-hidden border border-border max-w-2xl">
                                <img
                                  src={j.image}
                                  alt={j.role}
                                  data-testid={`job-image-${i}`}
                                  className="w-full max-h-72 object-cover"
                                />
                              </div>
                            ) : null}
                            {doneRole === j.role ? (
                              <div
                                data-testid={`job-applied-${i}`}
                                className="flex items-center gap-2 text-primary font-medium bg-secondary/10 rounded-2xl px-5 py-4"
                              >
                                <Check size={18} /> Application received. Thank you!
                              </div>
                            ) : (
                              <div className="max-w-2xl">
                                <div className="flex flex-col sm:flex-row gap-3">
                                  <input
                                    data-testid={`job-name-${i}`}
                                    placeholder="Your name"
                                    value={form.name}
                                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                                    className="flex-1 rounded-full border border-border bg-white px-5 py-3 text-sm outline-none focus:border-primary transition-colors"
                                  />
                                  <input
                                    data-testid={`job-email-${i}`}
                                    placeholder="Email"
                                    value={form.email}
                                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                                    className="flex-1 rounded-full border border-border bg-white px-5 py-3 text-sm outline-none focus:border-primary transition-colors"
                                  />
                                </div>
                                <div className="mt-3 flex flex-col sm:flex-row sm:items-center gap-3">
                                  <input
                                    ref={fileInputRef}
                                    type="file"
                                    accept=".pdf,.doc,.docx"
                                    onChange={pickResume}
                                    className="hidden"
                                    data-testid={`job-resume-input-${i}`}
                                  />
                                  {resumeFile ? (
                                    <span
                                      data-testid={`job-resume-name-${i}`}
                                      className="inline-flex items-center gap-2 rounded-full bg-secondary/15 text-primary px-4 py-2.5 text-sm font-medium max-w-full"
                                    >
                                      <Paperclip size={15} className="shrink-0" />
                                      <span className="truncate max-w-[200px]">{resumeFile.name}</span>
                                      <button
                                        type="button"
                                        onClick={() => setResumeFile(null)}
                                        data-testid={`job-resume-remove-${i}`}
                                        className="shrink-0 hover:text-accent transition-colors"
                                        aria-label="Remove résumé"
                                      >
                                        <X size={15} />
                                      </button>
                                    </span>
                                  ) : (
                                    <button
                                      type="button"
                                      data-testid={`job-resume-attach-${i}`}
                                      onClick={() => fileInputRef.current?.click()}
                                      className="inline-flex items-center gap-2 rounded-full border border-border bg-white px-5 py-2.5 text-sm font-medium text-foreground/70 hover:border-primary hover:text-primary transition-colors"
                                    >
                                      <Paperclip size={15} /> Attach résumé (PDF/DOC)
                                    </button>
                                  )}
                                  <button
                                    data-testid={`job-apply-${i}`}
                                    onClick={() => apply(j.role)}
                                    disabled={submitting}
                                    className="sm:ml-auto rounded-full bg-primary text-white px-6 py-3 text-sm font-medium hover:bg-[#0d5f57] transition-colors inline-flex items-center justify-center gap-2 disabled:opacity-60"
                                  >
                                    {submitting ? <Loader2 size={16} className="animate-spin" /> : "Apply"}
                                  </button>
                                </div>
                              </div>
                            )}
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
